import { BadgeCheck, ChevronsUpDown, Copy, LogOut } from "lucide-react";
import { toast } from "sonner";

import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import useOnboardingData from "@/store/onboarding-data";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Link } from "react-router";
import Cookies from "js-cookie";

export function NavUser() {
    const { isMobile } = useSidebar();

    const { userData, storeData } = useOnboardingData();

    const { user, logout } = useKindeAuth();

    const handleCopyCss = () => {
        const copyText = `<link rel="stylesheet" href="https://shoe-aichatbot-admin.vercel.app/assets/css/shoe-einstein-chatbot.css">`;

        navigator.clipboard.writeText(copyText);
        toast.success("CSS code copied to clipboard");
    };

    const handleCopyJs = () => {
        const copyText = `<script>
        window.widgetConfig = {
            __chat_id__: "${storeData?.id}",
        };
    </script>
    <script src="https://shoe-aichatbot-admin.vercel.app/assets/js/shoe-einstein-bot.umd.js"></script>`;

        navigator.clipboard.writeText(copyText);
        toast.success("JS code copied to clipboard");
    };

    const handleLogout = () => {
        const allCookies = Cookies.get();
        for (const cookie in allCookies) {
            Cookies.remove(cookie);
        }
        logout();
        localStorage.clear();
    };

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                            <Avatar className="size-8 rounded-lg border">
                                <AvatarImage src={user?.picture || ""} alt={userData?.name} />
                                <AvatarFallback className="rounded-lg capitalize">
                                    {(user && user.email !== null && user?.email[0]) || "G"}
                                </AvatarFallback>
                            </Avatar>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-semibold">{userData?.name}</span>
                                <span className="truncate text-xs">{userData?.email}</span>
                            </div>
                            <ChevronsUpDown className="ml-auto size-4" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                        side={isMobile ? "bottom" : "right"}
                        align="end"
                        sideOffset={4}
                    >
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                <Avatar className="size-8 rounded-lg">
                                    <AvatarImage src={user?.picture || ""} alt={userData?.name} />
                                    <AvatarFallback className="rounded-lg">{user ? user?.given_name : "G"}</AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">{userData?.name}</span>
                                    <span className="truncate text-xs">{userData?.email}</span>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <Link to={"/app/account-settings"} className="flex items-center gap-2">
                                <DropdownMenuItem className="w-full">
                                    <BadgeCheck className="size-4" />
                                    Account
                                </DropdownMenuItem>
                            </Link>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <DropdownMenuItem onClick={(e) => e.preventDefault()}>
                                <Dialog>
                                    <DialogTrigger>
                                        <div className="flex items-center gap-2">
                                            <Copy className="size-4" />
                                            Copy Embed Code
                                        </div>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Instructions on How to Use the Widget</DialogTitle>
                                            <DialogDescription>
                                                <Separator className="my-2" />
                                                <p className="font-bold">1. Copy the code bolow and add in the head of your website HTML file</p>
                                                <div className="relative mt-2 rounded-md border p-4 pt-8">
                                                    <Button
                                                        onClick={handleCopyCss}
                                                        size={"sm"}
                                                        variant={"outline"}
                                                        className="absolute right-0 top-0 rounded-br-none rounded-tl-none border-r-0 border-t-0"
                                                    >
                                                        <Copy />
                                                        Copy
                                                    </Button>
                                                    <code>{`<link rel="stylesheet" href="${window.origin}/assets/css/shoe-einstein-chatbot.css">`}</code>
                                                </div>
                                                <p className="mt-4 font-bold">
                                                    1. Copy the code bolow and add in the end of the body of your website HTML file
                                                </p>
                                                <div className="relative mt-2 rounded-md border p-4 pt-8">
                                                    <Button
                                                        onClick={handleCopyJs}
                                                        size={"sm"}
                                                        variant={"outline"}
                                                        className="absolute right-0 top-0 rounded-br-none rounded-tl-none border-r-0 border-t-0"
                                                    >
                                                        <Copy />
                                                        Copy
                                                    </Button>
                                                    <code>
                                                        {`
                            <script>
                                window.widgetConfig = {
                                    __chat_id__: "${storeData?.id}"
                                };
                            </script>`}
                                                    </code>
                                                    <code className="mt-2 inline-block">
                                                        {`
                            <script src="${window.origin}/assets/js/shoe-einstein-bot.umd.js"></script>`}
                                                    </code>
                                                </div>
                                            </DialogDescription>
                                        </DialogHeader>
                                    </DialogContent>
                                </Dialog>
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout}>
                            <LogOut />
                            Log out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    );
}
